import { motion } from "framer-motion";
import Seo from "../components/Seo";

export default function PrivacyPolicy({ serviceName }) {
	return (
		<>
			<Seo
				title={`${serviceName} - Политика конфиденциальности`}
				description="Умная защита от DDoS атак, ботов и кибератак."
			/>
			<div className="relative min-h-screen bg-gray-900 text-white">
				{/* Hero секция */}
				<section className="relative z-10 flex flex-col items-center justify-center min-h-screen text-center px-4">
					<motion.h1
						className="text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-green-400 via-blue-400 to-purple-400"
						initial={{ opacity: 0, y: -40 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 1 }}
					>
						Политика конфиденциальности {serviceName}
					</motion.h1>

					<motion.p
						className="text-lg md:text-xl text-gray-300 max-w-3xl mb-8"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.3, duration: 1 }}
					>
						Настоящая политика описывает, как {serviceName} собирает, использует и защищает персональные данные пользователей сервиса.
					</motion.p>
				</section>

				{/* Содержимое политики */}
				<section className="relative z-10 py-20 bg-gray-800/70 backdrop-blur-sm">
					<div className="max-w-5xl mx-auto px-6 space-y-12">

						{/* 01 */}
						<motion.div
							initial={{ opacity: 0, y: 40 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.8 }}
							className="space-y-6"
						>
							<h2 className="text-3xl font-bold text-green-400">01. Сбор информации</h2>
							<p className="text-gray-300 text-lg leading-relaxed">
								Мы собираем минимально необходимую информацию для работы сервиса, включая имя, адрес электронной почты и идентификатор аккаунта.
								Эти данные нужны для идентификации, управления учётной записью и предоставления доступа к услугам {serviceName}.
							</p>
							<p className="text-gray-300 text-lg leading-relaxed">
								Техническая информация (например, IP-адрес, страна подключения) не сохраняется.
							</p>
						</motion.div>

						{/* 02 */}
						<motion.div
							initial={{ opacity: 0, y: 40 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ delay: 0.2, duration: 0.8 }}
							className="space-y-6"
						>
							<h2 className="text-3xl font-bold text-green-400">02. Использование информации</h2>
							<p className="text-gray-300 text-lg leading-relaxed">
								Собранные данные используются исключительно для:
							</p>
							<ul className="list-disc list-inside text-gray-300 space-y-2">
								<li>предоставления доступа к сервису;</li>
								<li>оказания технической поддержки;</li>
								<li>повышения качества работы {serviceName}.</li>
							</ul>
							<p className="text-gray-300 text-lg leading-relaxed">
								Мы не передаём персональные данные третьим лицам.
							</p>
						</motion.div>

						{/* 03 */}
						<motion.div
							initial={{ opacity: 0, y: 40 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ delay: 0.4, duration: 0.8 }}
							className="space-y-6"
						>
							<h2 className="text-3xl font-bold text-green-400">03. Хранение и защита данных</h2>
							<p className="text-gray-300 text-lg leading-relaxed">
								Все персональные данные хранятся в зашифрованном виде и недоступны третьим лицам.
								Мы применяем современные технические и организационные меры безопасности для их защиты.
							</p>
						</motion.div>

						{/* 04 */}
						<motion.div
							initial={{ opacity: 0, y: 40 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ delay: 0.6, duration: 0.8 }}
							className="space-y-6"
						>
							<h2 className="text-3xl font-bold text-green-400">04. Cookies и сторонние сервисы</h2>
							<p className="text-gray-300 text-lg leading-relaxed">
								Мы не используем сторонние аналитические системы и не устанавливаем cookies.
								Взаимодействие ограничено только API и прямыми подключениями к серверам {serviceName}.
							</p>
						</motion.div>

						{/* 05 */}
						<motion.div
							initial={{ opacity: 0, y: 40 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ delay: 0.8, duration: 0.8 }}
							className="space-y-6"
						>
							<h2 className="text-3xl font-bold text-green-400">05. Права пользователя</h2>
							<p className="text-gray-300 text-lg leading-relaxed">
								Пользователь имеет право запросить удаление или изменение своих персональных данных.
								В отдельных случаях, необходимых для соблюдения политики безопасности или корректной работы сервиса,
								часть информации может быть сохранена в ограниченном виде.
							</p>
						</motion.div>

						{/* 06 */}
						<motion.div
							initial={{ opacity: 0, y: 40 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ delay: 1.0, duration: 0.8 }}
							className="space-y-6"
						>
							<h2 className="text-3xl font-bold text-green-400">06. Изменения политики</h2>
							<p className="text-gray-300 text-lg leading-relaxed">
								Мы оставляем за собой право изменять настоящую политику конфиденциальности без предварительного уведомления.
								Актуальная версия всегда публикуется на сайте {serviceName}.
							</p>
						</motion.div>

						{/* 07 */}
						<motion.div
							initial={{ opacity: 0, y: 40 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ delay: 1.2, duration: 0.8 }}
							className="space-y-6"
						>
							<h2 className="text-3xl font-bold text-green-400">07. Контакты</h2>
							<p className="text-gray-300 text-lg leading-relaxed">
								По вопросам, связанным с конфиденциальностью и защитой данных, вы можете связаться с нами:
							</p>
							<p className="text-gray-300 text-lg leading-relaxed">
								Email: <a href="mailto:info@shieldai.ru" className="text-green-400 hover:underline">info@shieldai.ru</a>
							</p>
							<p className="text-gray-300 text-lg leading-relaxed">
								Телеграм: <a href="https://t.me/shield_ai_bot" className="text-green-400 hover:underline">@shield_ai_bot</a>
							</p>
							<p className="text-gray-300 text-lg leading-relaxed">
								Сайт: <a href="/" className="text-green-400 hover:underline">{serviceName}</a>
							</p>
							<p className="text-gray-300 text-lg leading-relaxed">
								ИНН: <span className="text-gray-200">770906897993</span>
							</p>
						</motion.div>

						{/* CTA */}
						<motion.div
							initial={{ opacity: 0, y: 40 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ delay: 1.4, duration: 0.8 }}
							className="text-center mt-16"
						>
							<a
								href="/dashboard"
								className="px-8 py-4 bg-gradient-to-r from-green-500 to-blue-600 hover:from-blue-500 hover:to-purple-500 rounded-2xl text-lg font-semibold shadow-2xl transition-all duration-300 hover:scale-105"
							>
								Перейти в личный кабинет
							</a>
						</motion.div>
					</div>
				</section>
			</div>
		</>
	);
}

